import React, { useState } from 'react';
import { Icon } from '../../assets/icons/Icon';
import Button from '../button/Button';
import { BehaviourWithHabitsResponse } from '../../api/Api';
import styles from './styles.module.scss';

interface HabitSelectStepProps {
  selectedHabits: string[];
  setSelectedHabits: React.Dispatch<React.SetStateAction<string[]>>;
  currentStep: number;
  onStepChange: (step: number) => void;
  selectedBehaviour?: BehaviourWithHabitsResponse | null;
  onBack?: () => void;
  programmeId?: string;
  moduleId?: string;
  maxConcurrentHabits?: number;
  showStepper?: boolean;
}

const HabitSelectStep: React.FC<HabitSelectStepProps> = ({
  selectedHabits,
  setSelectedHabits,
  selectedBehaviour,
  maxConcurrentHabits = 1,
  showStepper = true,
}) => {
  const [expandedTips, setExpandedTips] = useState<Set<string>>(new Set());

  const handleHabitToggle = (habitId: string) => {
    setSelectedHabits((prev) => {
      if (prev.includes(habitId)) {
        return prev.filter((id) => id !== habitId);
      }
      if (prev.length >= maxConcurrentHabits) {
        return prev;
      }
      return [...prev, habitId];
    });
  };

  const toggleTips = (habitId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedTips((prev) => {
      const next = new Set(prev);
      if (next.has(habitId)) {
        next.delete(habitId);
      } else {
        next.add(habitId);
      }
      return next;
    });
  };

  if (!selectedBehaviour) {
    return (
      <div className={styles.habitSelectStep}>
        <p>No behaviour selected</p>
      </div>
    );
  }

  const limitReached = selectedHabits.length >= maxConcurrentHabits;

  return (
    <div className={styles.habitsBlock}>
      {showStepper && (
        <div className={styles.stepper}>
          <div className={styles.stepperItem}>
            <div className={`${styles.stepperCircle} ${styles.stepperCircleDone}`}>1</div>
            <span className={styles.stepperLabel}>Behaviour selection</span>
          </div>
          <div className={styles.stepperLine} />
          <div className={styles.stepperItem}>
            <div className={`${styles.stepperCircle} ${styles.stepperCircleActive}`}>2</div>
            <span className={styles.stepperLabel}>Habit selection</span>
          </div>
        </div>
      )}

      <div className={styles.habitsBlockHeader}>
        <h2 className={styles.habitsBlockTitle}>Select habit to develop</h2>
        <p className={styles.habitsBlockDescription}>{selectedBehaviour.name}</p>
      </div>

      <div className={styles.habitsBlockSection}>
        <div className={styles.habitsBlockSectionHeader}>
          <div className={styles.habitsBlockSectionHeaderTitle}>
            Please select {maxConcurrentHabits} habit{maxConcurrentHabits !== 1 ? 's' : ''} to develop
          </div>
          <div className={styles.habitsBlockSectionHeaderCounter}>
            SELECTED {selectedHabits.length}/{maxConcurrentHabits}
          </div>
        </div>

        <div className={styles.habitsBlockSectionItems}>
          {selectedBehaviour.habits?.map((habit) => {
            const isSelected = selectedHabits.includes(habit.id);
            const isDisabled = limitReached && !isSelected;
            const isTipsExpanded = expandedTips.has(habit.id);

            return (
              <div
                key={habit.id}
                className={`${styles.habitsBlockSectionItem} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
                onClick={() => !isDisabled && handleHabitToggle(habit.id)}
              >
                <div className={styles.habitsBlockSectionItemContent}>
                  <div className={styles.habitsBlockSectionItemContentLeft}>
                    <div className={styles.habitsBlockSectionItemContentIcon}>
                      <Icon iconName="OneIcon" width="3.5rem" height="3.5rem" />
                    </div>
                    <div className={styles.habitsBlockSectionItemContentBody}>
                      <h3>{habit.name}</h3>
                      {habit.description && <p>{habit.description}</p>}
                      {habit.tips && (
                        <button
                          className={styles.showMoreBtn}
                          onClick={(e) => toggleTips(habit.id, e)}
                        >
                          {isTipsExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                      {isTipsExpanded && habit.tips && (
                        <p className={styles.habitTipsText}>{habit.tips}</p>
                      )}
                    </div>
                  </div>
                  <div className={styles.habitsBlockSectionItemContentAction}>
                    {isSelected ? (
                      <div className={styles.habitsBlockSectionItemContentSelected}>
                        <Icon iconName="GreenCheckIcon2" width="1rem" height="1rem" />
                        <div className={styles.habitsBlockSectionItemContentCancel}>Cancel selection</div>
                      </div>
                    ) : (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDisabled) handleHabitToggle(habit.id);
                        }}
                        variant={isDisabled ? 'secondary' : 'outline'}
                        size="small"
                        disabled={isDisabled}
                      >
                        Select
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HabitSelectStep;
